import { useCallback, useState } from "react";
import { renderCMSComponent } from "~/features/cms/componentFactory";
import type { CMSPhotoGalleryDTO } from "~/features/cms/types/dto";
import classes from "~/features/cms/components/cmsImage/CMSPhotoGallery.module.less";
import classNames from "classnames";
import { IconButton } from "~/common/mui";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { SIZE_DESKTOP_S, useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { makeExternallyStyledBaseElement } from "~/features/cms/utils/styles";

interface Props extends Omit<CMSPhotoGalleryDTO, "type"> {
  className?: string;
}

export const CMSPhotoGallery = (props: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const { images } = props;

  // Determine window view
  const size = useNumericScreenSize();
  const isDesktop = size >= SIZE_DESKTOP_S;

  // Determine number of items in view
  const numberOfItemsInView = isDesktop ? 4 : 3;

  const isNextDisabled = () => {
    if (images.length == 3) {
      // special case for n = 3
      return activeIndex == 2;
    }
    return activeIndex > images.length - numberOfItemsInView;
  };

  const isPrevDisabled = () => {
    return activeIndex <= 0;
  };

  // Navigation handlers
  const nextSlide = () => {
    if (images.length == 3) {
      // special case for n = 3
      setActiveIndex((prev) => prev + 2);
    } else if (isNextDisabled()) {
      return;
    } else if (activeIndex == images.length - numberOfItemsInView) {
      // navigate 2 step forward to reach end if about to reach end of the list
      setActiveIndex((prev) => prev + 2);
    } else {
      setActiveIndex((prev) => prev + 1); // navigate 1 step forward
    }
  };

  const prevSlide = () => {
    if (images.length == 3) {
      setActiveIndex((prev) => prev - 2);
    } else if (isPrevDisabled()) {
      return;
    } else if (activeIndex == images.length - numberOfItemsInView + 2) {
      // navigate 2 step back if already at the end of the list
      setActiveIndex((prev) => prev - 2);
    } else {
      setActiveIndex((prev) => prev - 1); // navigate 1 step back
    }
  };

  // Get styles dynamically
  const getWidth = useCallback(
    (index: number) => {
      const remainingItems = images.length - activeIndex;
      // give 100% width each image if n < 3
      if (images.length < 3) {
        return "100%";
      } else if (images.length == 3) {
        // special case for n = 3
        const specialWidths = ["100%", "100%", "56px"];
        return activeIndex === 0 ? specialWidths[index] : specialWidths.reverse()[index];
      } else if (remainingItems < numberOfItemsInView) {
        // if user scrolled to end of images list
        if (index === images.length - 1) {
          return "100%";
        }
        if (index === images.length - 2) {
          return "100%";
        }
        if (index === images.length - 3) {
          return isDesktop ? "120px" : "56px";
        }
        if (index === images.length - 4 && isDesktop) {
          return "56px";
        }
        return "0%";
      } else {
        // default case [100% 100% 120px 56px] or [100% 100% 120px]
        if (index === 0) {
          return "100%";
        }
        if (index === 1) {
          return "100%";
        }
        if (index === 2) {
          return isDesktop ? "120px" : "56px";
        }
        if (index === 3 && isDesktop) {
          return "56px";
        }
      }
      return "0%";
    },
    [activeIndex, images.length, numberOfItemsInView, size],
  );

  const getMargin = useCallback(
    (relativeIndex: number, index: number) => {
      const remainingItems = images.length - activeIndex + 1;
      if (remainingItems <= numberOfItemsInView) {
        return index > images.length - numberOfItemsInView - 1 && index != images.length - 1 ? "8px" : "0px";
      } else {
        return relativeIndex < numberOfItemsInView - 1 && index != images.length - 1 ? "8px" : "0px";
      }
    },
    [activeIndex, images.length, numberOfItemsInView, size],
  );

  // Detect Swipe
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEndX(e.touches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    const swipeDistance = touchStartX - touchEndX;
    const swipeThreshold = 50;

    if (Math.abs(touchEndX) > swipeThreshold) {
      if (swipeDistance > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }

    setTouchStartX(0);
    setTouchEndX(0);
  }, [touchStartX, touchEndX, nextSlide, prevSlide]);

  return (
    <RootElement className={classNames(classes.carouselContainer)} rootStyles={props.rootStyles}>
      <div
        className={classNames(classes.carousel)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {props.images.map((image, index) => {
          const relativeIndex = (index - activeIndex + images.length) % images.length;
          const getWidthParam = activeIndex > images.length - numberOfItemsInView ? index : relativeIndex;

          return renderCMSComponent(
            {
              ...image,
              relativeIndex: relativeIndex,
              getWidthParam: getWidthParam,
              getWidth: getWidth,
              getMargin: getMargin,
              index,
            },
            undefined,
            index,
          );
        })}
      </div>
      {isDesktop && !isPrevDisabled() && (
        <IconButton
          variant="contained"
          color={"secondary"}
          size={"large"}
          tabIndex={-1}
          onClick={prevSlide}
          className={classNames(classes.leftIcon)}
        >
          <Icon type={IconType.ChevronLeft} />
        </IconButton>
      )}
      {isDesktop && !isNextDisabled() && (
        <IconButton
          variant="contained"
          color={"secondary"}
          size={"large"}
          tabIndex={-1}
          onClick={nextSlide}
          className={classNames(classes.rightIcon)}
        >
          <Icon type={IconType.ChevronRight} />
        </IconButton>
      )}
    </RootElement>
  );
};

const RootElement = makeExternallyStyledBaseElement("div");
